import React from 'react';
import { Database, Shield, RefreshCw, AlertCircle, ExternalLink, CheckCircle } from 'lucide-react';
export function MethodologyPage() {
  const dataSources = [{
    name: 'Blockchain.com',
    url: 'https://www.blockchain.com/explorer',
    description: 'Primary blockchain explorer for wallet balances and transaction data'
  }, {
    name: 'Blockchair',
    url: 'https://blockchair.com/bitcoin',
    description: 'Secondary verification source for blockchain data and analytics'
  }, {
    name: 'BitInfoCharts',
    url: 'https://bitinfocharts.com/bitcoin',
    description: 'Statistical data and historical wallet information'
  }, {
    name: 'Glassnode',
    url: 'https://glassnode.com',
    description: 'On-chain analytics and market intelligence'
  }];
  const methodology = [{
    title: 'Data Collection',
    icon: Database,
    points: ['All wallet addresses are sourced from public blockchain explorers', 'Balances are verified across multiple independent sources', 'Data is updated regularly to reflect current blockchain state', 'No private or non-public information is collected or displayed']
  }, {
    title: 'Verification Process',
    icon: CheckCircle,
    points: ['Each wallet is cross-referenced with at least 2 blockchain explorers', 'Balance discrepancies are investigated and resolved', 'Entity labels are based on publicly known information only', 'Unknown wallets are clearly marked as such']
  }, {
    title: 'Update Frequency',
    icon: RefreshCw,
    points: ['Wallet balances are updated every 24 hours', 'Major movements (>1000 BTC) trigger immediate updates', 'Historical data is preserved for trend analysis', 'Last update timestamp is displayed on each page']
  }, {
    title: 'Privacy & Ethics',
    icon: Shield,
    points: ['Only publicly available blockchain data is used', 'No attempt to de-anonymize unknown wallet owners', 'Clear disclaimers about data accuracy and limitations', 'Educational purpose with transparent methodology']
  }];
  return <div className="min-h-screen bg-gray-950" data-id="element-211">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-212">
        {/* Header */}
        <div className="mb-12" data-id="element-213">
          <h1 className="text-4xl font-bold text-white mb-4" data-id="element-214">
            Methodology & Data Sources
          </h1>
          <p className="text-gray-400 text-lg" data-id="element-215">
            Transparency is core to our mission. Learn how we collect, verify,
            and present Bitcoin wallet data.
          </p>
        </div>

        {/* Methodology Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-216">
          {methodology.map(section => {
          const Icon = section.icon;
          return <div key={section.title} className="bg-gray-800/50 border border-gray-700 rounded-xl p-6" data-id="element-217">
                <div className="flex items-center gap-3 mb-4" data-id="element-218">
                  <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center" data-id="element-219">
                    <Icon className="w-5 h-5 text-orange-500" data-id="element-220" />
                  </div>
                  <h2 className="text-xl font-bold text-white" data-id="element-221">
                    {section.title}
                  </h2>
                </div>
                <ul className="space-y-2" data-id="element-222">
                  {section.points.map((point, index) => <li key={index} className="flex items-start gap-2 text-gray-400 text-sm" data-id="element-223">
                      <span className="text-orange-500 mt-1" data-id="element-224">•</span>
                      <span data-id="element-225">{point}</span>
                    </li>)}
                </ul>
              </div>;
        })}
        </div>

        {/* Data Sources */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-8 mb-12" data-id="element-226">
          <h2 className="text-2xl font-bold text-white mb-6" data-id="element-227">
            Primary Data Sources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-228">
            {dataSources.map(source => <div key={source.name} className="bg-gray-900/50 rounded-lg p-6" data-id="element-229">
                <div className="flex items-start justify-between mb-3" data-id="element-230">
                  <h3 className="text-white font-bold" data-id="element-231">{source.name}</h3>
                  <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-400 transition-colors" data-id="element-232">
                    <ExternalLink className="w-5 h-5" data-id="element-233" />
                  </a>
                </div>
                <p className="text-gray-400 text-sm" data-id="element-234">{source.description}</p>
              </div>)}
          </div>
        </div>

        {/* Exclusions */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-8 mb-12" data-id="element-235">
          <h2 className="text-2xl font-bold text-white mb-6" data-id="element-236">
            What We Exclude
          </h2>
          <div className="space-y-4" data-id="element-237">
            <div className="flex items-start gap-3" data-id="element-238">
              <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" data-id="element-239" />
              <div data-id="element-240">
                <h3 className="text-white font-bold mb-1" data-id="element-241">
                  Provably Lost Coins
                </h3>
                <p className="text-gray-400 text-sm" data-id="element-242">
                  Wallets with no activity since early Bitcoin days (2009-2010)
                  are marked as "likely lost" but still included for historical
                  accuracy.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-243">
              <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" data-id="element-244" />
              <div data-id="element-245">
                <h3 className="text-white font-bold mb-1" data-id="element-246">Burn Addresses</h3>
                <p className="text-gray-400 text-sm" data-id="element-247">
                  Known burn addresses (e.g.,
                  1BitcoinEaterAddressDontSendf59kuE) are excluded as they
                  represent destroyed coins.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-248">
              <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" data-id="element-249" />
              <div data-id="element-250">
                <h3 className="text-white font-bold mb-1" data-id="element-251">
                  Duplicate Addresses
                </h3>
                <p className="text-gray-400 text-sm" data-id="element-252">
                  If an entity controls multiple addresses, each is listed
                  separately to maintain transparency about distribution.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Limitations */}
        <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-8" data-id="element-253">
          <div className="flex items-start gap-4" data-id="element-254">
            <AlertCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" data-id="element-255" />
            <div data-id="element-256">
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-257">
                Limitations & Disclaimers
              </h2>
              <ul className="space-y-2 text-gray-300 text-sm" data-id="element-258">
                <li data-id="element-259">
                  • Entity labels are based on publicly available information
                  and may not be 100% accurate
                </li>
                <li data-id="element-260">
                  • Some exchange wallets may be misidentified or unlabeled
                </li>
                <li data-id="element-261">
                  • Balance data reflects blockchain state at time of last
                  update
                </li>
                <li data-id="element-262">
                  • USD values are estimates based on current market prices
                </li>
                <li data-id="element-263">
                  • This data is for educational and research purposes only
                </li>
                <li data-id="element-264">
                  • Always verify information through multiple independent
                  sources
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>;
}